import { useState, useEffect } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Input, Text } from '@/components/ui';
import { authApi } from '@/lib/auth/api';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { Logo } from '@/assets/images/Logo';
import { toast } from 'sonner';

const createResetPasswordSchema = (t: any) =>
  z
    .object({
      newPassword: z.string().min(6, t('auth.passwordMinLength')),
      confirmPassword: z.string().min(6, t('auth.passwordMinLength')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('auth.passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });

type ResetPasswordForm = z.infer<ReturnType<typeof createResetPasswordSchema>>;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  const token = searchParams.get('token');
  const resetPasswordSchema = createResetPasswordSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    if (!token) {
      toast.error(t('auth.noResetToken'));
    }
  }, [token, t]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(data: ResetPasswordForm) {
    if (!token) return;

    setIsLoading(true);
    try {
      await authApi.resetPassword({
        token,
        newPassword: data.newPassword,
      });
      setIsPasswordReset(true);
      toast.success(t('auth.passwordResetSuccess'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.resetPasswordError'));
    } finally {
      setIsLoading(false);
    }
  }

  if (isPasswordReset) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="bg-base-white border-transparent-dark-12 p-lg flex w-96 flex-col rounded-3xl border shadow-xs">
          <Logo className="self-center" />

          <div className="mt-2xl text-center">
            <div className="mb-md mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <Text variant="label" size="xl" strong className="text-center">
              {t('auth.passwordResetSuccess')}
            </Text>
            <Text className="text-neutral-muted mt-2xs">
              {t('auth.passwordResetSuccessDescription')}
            </Text>
          </div>

          <Link to="/login">
            <Button variant="secondary" className="mt-2xl w-full">
              <ArrowLeftIcon size={16} />
              {t('auth.backToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="bg-base-white border-transparent-dark-12 p-lg flex w-96 flex-col rounded-3xl border shadow-xs">
        <Logo className="self-center" />

        <div className="mt-2xl">
          <Text variant="label" size="xl" strong className="text-center">
            {t('auth.resetPassword')}
          </Text>
          <Text className="text-neutral-muted mt-2xs text-center">
            {t('auth.resetPasswordDescription')}
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-xl flex flex-col" noValidate>
          <Input
            {...register('newPassword')}
            type="password"
            label={t('auth.newPassword')}
            placeholder={t('auth.newPasswordPlaceholder')}
            error={errors.newPassword?.message}
            disabled={isLoading}
          />

          <Input
            {...register('confirmPassword')}
            type="password"
            label={t('auth.confirmPassword')}
            placeholder={t('auth.confirmPasswordPlaceholder')}
            className="mt-md"
            error={errors.confirmPassword?.message}
            disabled={isLoading}
          />

          <Button type="submit" className="mt-2xl" loading={isLoading}>
            {t('auth.resetPassword')}
          </Button>

          <Link to="/login">
            <Text
              variant="label"
              strong
              className="text-neutral-muted mt-md hover:text-neutral-default gap-2xs flex cursor-pointer items-center justify-center transition-colors duration-150 ease-in-out"
              role="button"
            >
              <ArrowLeftIcon size={16} />
              {t('auth.backToLogin')}
            </Text>
          </Link>
        </form>
      </div>
    </div>
  );
}
